import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';

import {
    getLoanFormatAllAmount,
    getLoanFormatEAudiobooksAmount,
    getLoanFormatEbooksAmount,
    getLoanFormatEMagazineAmount,
    reorderProductsInList
} from './sortingHelper';
import { ProductList } from './ProductList';
import { LoadingWrapper } from '../../../../shared/components/LoadingWrapper';
import { ProductGroupTypeWithAmounts } from './ProductGroupTypeWithAmounts';
import { SortingHeadLine } from './SortingHeadline';
import LoadingOverlay from '../../../../shared/components/LoadingOverlay';
import { ProductOut } from '../../../../store/api';
import { useLibraryManagedProductGroupProducts } from '../../../../api/library-managed/useLibraryManagedProductGroupProducts';
import { useHistory, useParams } from 'react-router';
import { useLibraryManagedProductGroupsFeaturePages } from '../api/useLibraryManagedProductGroupsFeaturePages';
import { DEFAULT_SORTBY, PAGE, PAGE_SIZE } from './api/apiSaveLibraryManagedProductGroupSorting';
import { useSaveLibraryManagedProductGroupSorting } from './api/useSaveLibraryManagedProductGroupSorting';
import { useTranslation } from 'react-i18next';
import { FONT_NORMAL, themeFontSize } from '../../../../shared/common/fontSizes';
import { THEME_SECONDARY, themeColor } from '../../../../styles/basics/colors';
import { ButtonTextWidthAuto } from '../../../../shared/common/Button/ButtonTextWidthAuto';
import {
    BUTTON_SIZE_MEDIUM,
    BUTTON_THEME_EMPTY_GREEN,
    BUTTON_THEME_FULL_GREEN
} from '../../../../shared/common/Button/themes';

const StTitleInfoLine = styled.div`
    display: flex !important;
    align-items: center;
    justify-content: flex-end;
    margin-right: 40px;
`;

const StButtonWrapper = styled.div`
    padding-left: 20px;

    button {
        margin-right: 15px;
    }

    display: flex;
`;
const StHeadlineWrapper = styled.div`
    flex-grow: 1;
    margin-right: 10px;
`;

const StProductsAmountLine = styled.div`
    font-size: ${themeFontSize(FONT_NORMAL)};
    color: ${themeColor(THEME_SECONDARY)};
    padding-left: 30px;
    padding-top: 30px;
    padding-bottom: 30px;
`;

export type CurrentProductList = ProductOut & { rank: number };

export const SortingView: FC = () => {
    const { t } = useTranslation();
    const { productGroupId } = useParams<{ productGroupId: string }>();
    const history = useHistory();

    const [productsToMove, setProductsToMove] = useState<CurrentProductList[]>([]);
    const [currentProductList, setCurrentProductList] = useState<CurrentProductList[] | undefined>([]);
    const [isCurrentListUnsaved, setCurrentListToUnsavedState] = useState(false);

    const { data: featuredPages, isLoading: isLoadingFeaturePages } = useLibraryManagedProductGroupsFeaturePages();
    const featuredPage = featuredPages?.find(page =>
        page?.productGroupSlots?.find(slot => slot?.productGroup?.id === parseInt(productGroupId))
    );
    const productGroupSlot = featuredPage?.productGroupSlots?.find(
        slot => slot?.productGroup?.id === parseInt(productGroupId)
    );
    const productGroup = productGroupSlot?.productGroup;

    const { data: products, isInitialLoading: isLoadingProducts } = useLibraryManagedProductGroupProducts({
        productGroupId,
        pagination: {
            sortBy: DEFAULT_SORTBY,
            page: PAGE,
            pageSize: PAGE_SIZE
        }
    });

    const handleCancel = () => {
        setCurrentProductList(products?.hits as CurrentProductList[]);
        setCurrentListToUnsavedState(false);
        history.push(`/marketing/productgroups/${productGroupId}`);
    };

    const saveSortingMutation = useSaveLibraryManagedProductGroupSorting(productGroupId);
    const saveSorting = () => {
        const productIdsToSortPosition =
            currentProductList?.reduce((acc, curr, index: number) => ({ ...acc, [curr.id as string]: index }), {}) ??
            {};
        saveSortingMutation.mutate({ productGroupId, productIdsToSortPosition });
        setCurrentListToUnsavedState(false);
    };

    useEffect(() => {
        setCurrentProductList(products?.hits as CurrentProductList[]);
    }, [products?.hits]);

    function updateProductsToMove(product: CurrentProductList) {
        const isProductInList = productsToMove.some(curr => JSON.stringify(curr) === JSON.stringify(product));

        if (!isProductInList) {
            const updatedList: CurrentProductList[] = [...productsToMove];
            updatedList.push(product);
            setProductsToMove(updatedList);
        } else {
            const updatedList = productsToMove.filter(curr => JSON.stringify(curr) !== JSON.stringify(product));
            setProductsToMove(updatedList);
        }
    }

    function handleStatefulMove(indexInList: number) {
        if (productsToMove && productsToMove.length > 0) {
            const reorderedList = reorderProductsInList(currentProductList!, productsToMove, indexInList);
            if (isEqual(reorderedList, currentProductList)) return;
            setCurrentProductList(reorderedList);
            setProductsToMove([]);
            setCurrentListToUnsavedState(true);
        }
    }

    function handleStatelessMove(currentProductRank: number, nextProductRank: number) {
        const nextProductList: CurrentProductList[] = [...currentProductList!];
        const element = currentProductList![currentProductRank];

        nextProductList.splice(currentProductRank, 1);
        nextProductList.splice(nextProductRank, 0, element);

        const nextProductListWithRanks = nextProductList.map((product, index) => ({
            ...product,
            rank: index
        }));

        setCurrentProductList(nextProductListWithRanks);
        setCurrentListToUnsavedState(true);
    }

    return (
        <>
            <LoadingWrapper>
                <LoadingOverlay loading={isLoadingProducts || isLoadingFeaturePages} />
            </LoadingWrapper>
            <StTitleInfoLine>
                <StHeadlineWrapper>
                    <SortingHeadLine title={productGroup?.title} />
                </StHeadlineWrapper>
                <ProductGroupTypeWithAmounts
                    ebooksAmount={getLoanFormatEbooksAmount(currentProductList)}
                    eaudiobooksAmount={getLoanFormatEAudiobooksAmount(currentProductList)}
                    epressAmount={getLoanFormatEMagazineAmount(currentProductList)}
                />

                <StButtonWrapper>
                    <ButtonTextWidthAuto
                        onClick={handleCancel}
                        theme={BUTTON_THEME_EMPTY_GREEN}
                        disabled={!isCurrentListUnsaved}
                        size={BUTTON_SIZE_MEDIUM}
                    >
                        {t('cancel')}
                    </ButtonTextWidthAuto>
                    <ButtonTextWidthAuto
                        onClick={saveSorting}
                        theme={BUTTON_THEME_FULL_GREEN}
                        disabled={!isCurrentListUnsaved}
                        size={BUTTON_SIZE_MEDIUM}
                    >
                        {t('saveChanges')}
                    </ButtonTextWidthAuto>
                </StButtonWrapper>
            </StTitleInfoLine>
            <StProductsAmountLine>
                {t('product.productWithCount', { count: getLoanFormatAllAmount(currentProductList) ?? 0 })}
            </StProductsAmountLine>

            <ProductList
                handleStatefulMove={handleStatefulMove}
                handleStatelessMove={handleStatelessMove}
                productList={currentProductList}
                handleProductSelection={updateProductsToMove}
                checkedProducts={productsToMove}
            />
        </>
    );
};
